/**
 * TopToolbar Component
 * Main actions and workflow controls
 * Integrates three-mode designer (Design/Execution/Evaluation)
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useWorkflowStore } from '@flow-studio/store'; // import { workflowStore';
import { TrashIcon, SaveIcon, ZapIcon, EyeIcon } from '@bizfirst/common-react';
import { CheckIcon } from '../Icons';
import { useUIStore } from '@flow-studio/store'; // import { uiStore';
import { useDesignerModeStore } from '@flow-studio/store'; // import { designerModeStore';
import { useSettingsStore } from '@flow-studio/store'; // import { settingsStore';
import { useExecutionControls } from '../../hooks/useExecutionControls';
import { useExecutionNotifications } from '../../hooks/useExecutionNotifications';
import { ConfirmDialog } from '../Support/ConfirmDialog';
import { ModeSelector } from './ModeSelector';
import { processEngineExecutionApiClient, apiConfig } from '@flow-studio/api';
import { ExecutionPipelinesApiClient, ExecutionTemplate } from 'process-pipelines-common';
import { FlowObserverPanelEngine, useFlowObserverPanelStore } from '@flow-studio/flow-observer-core';
import { TemplateEditorModal, type ScopeContext as ModalScopeContext } from 'process-pipelines-editor';
import { ManageExecutionTemplatesModal } from './ManageExecutionTemplatesModal';
import { useCreateTemplate, type ScopeContext } from '../../hooks/useCreateTemplate';
import { DownloadFlowEntityModal } from '../Modals/DownloadFlowEntityModal';
import { AdvancedExecuteModal } from '../Modals/AdvancedExecuteModal';
import {
  ExecutionMode,
  EnvironmentMode,
  DesignerMode,
  type ExecuteProcessRequest,
  type ProcessContextExecutionMode,
  type ObservabilityOverride,
} from '@flow-studio/api';

// POST /api/v1/process-engine/execution/execute is synchronous on the backend
// (BaseProcessEngineExecutionController.Execute awaits ProcessEngineService.ExecuteProcessAsync
// to completion before responding — it is NOT a fire-and-forget kickoff call, despite this
// endpoint conceptually being one). AI Agent workflows can legitimately take from ~10 seconds to
// several minutes (LLM calls, MCP tool round trips), so this call needs a much longer client-side
// timeout than BaseApiClient's shared 30s default (apiConfig.timeout) — that default is sized for
// ordinary CRUD calls and was aborting healthy long-running executions client-side while the
// backend kept running them to completion regardless. 10 minutes covers realistic worst-case
// AI Agent runs; if it's still exceeded, the catch/error branch below surfaces a clear timeout
// toast instead of leaving the button stuck on "Executing...".
const EXECUTE_WORKFLOW_TIMEOUT_MS = 10 * 60 * 1000;

interface TopToolbarProps {
  // No onSave prop - using store directly
}

export const TopToolbar: React.FC<TopToolbarProps> = () => {
  const {
    workflow,
    nodes,
    canUndo,
    canRedo,
    undo,
    redo,
    isDirty,
    setNodes,
    setEdges,
    markDirty,
    pushHistory,
    // API integration
    isSaving,
    saveError,
    saveWorkflowToApi,
    currentProcessThreadId,
    currentVersionId,
  } = useWorkflowStore();
  const { theme, showMinimap, setShowMinimap, showToast, observeActive, setObserveActive, hideSatellites, setHideSatellites, hideUnassignedPorts, setHideUnassignedPorts, allNodesCollapsed, setAllNodesCollapsed, hilFormsVisible, toggleHilFormsVisible, hilChatVisible, toggleHilChatVisible } = useUIStore();
  const selectedNodeIds = (useUIStore() as any).selectedNodeIds as string[] | undefined;
  const nodeResizeEnabled = (useUIStore() as any).nodeResizeEnabled as boolean | undefined;
  const toggleNodeResizeEnabled = (useUIStore() as any).toggleNodeResizeEnabled as (() => void) | undefined;
  const autoSaveEnabled = (useUIStore() as any).autoSaveEnabled as boolean;
  const toggleAutoSave = (useUIStore() as any).toggleAutoSave as () => void;
  const hideComponentsGlobally = (useUIStore() as any).hideComponentsGlobally as boolean;
  const toggleHideComponentsGlobally = (useUIStore() as any).toggleHideComponentsGlobally as () => void;

  // Three-mode designer stores
  const mode = useDesignerModeStore((s) => s.mode);
  const setMode = useDesignerModeStore((s) => s.setMode);
  const executionState = useDesignerModeStore((s) => s.executionState);
  const startExecution = useDesignerModeStore((s) => s.startExecution);
  const setExecutionNumericId = useDesignerModeStore((s) => s.setExecutionNumericId);
  // @ts-ignore TS6133 - reserved for future use
  const _completeExecution = useDesignerModeStore((s) => s.completeExecution);
  const setExecutionResult = useDesignerModeStore((s) => s.setExecutionResult);
  const clearExecutionState = useDesignerModeStore((s) => s.clearExecutionState);

  // Settings store
  const settings = useSettingsStore((s) => s.settings);

  // Execution control hooks
  const { handlePause, handleResume, handleCancel } = useExecutionControls();
  const notifications = useExecutionNotifications();

  // Auto-open observe panel when entering evaluation mode (e.g. clicking a past
  // execution in the Executions browser) if it was previously active. Gated the
  // same way as the WorkflowStarted nav-jump fix (§5.1, useFlowObserverSignalR.ts):
  // once the user has manually picked a tab this session, entering evaluation mode
  // must not yank them away from it.
  // Without this gate, `mode` only transitions 'design'->'evaluation' ONCE per session (picking
  // a second/third history item just updates selectedHistoryExecutionId, not mode itself), which
  // is exactly why this only force-focused Execution Status on the very first click and not later ones.
  //
  // NOTE: deliberately excludes mode === 'execution'. The only place `mode` transitions
  // to 'execution' is handleExecute below, which already sets observeActive + activateTab
  // ('dashboard') itself right in the handler. Including 'execution' here used to race
  // that explicit call: this effect runs after commit (later than the handler's synchronous
  // activateTab('dashboard')) and, whenever hasUserSelectedTab was still false (e.g. a fresh
  // session's first run), it clobbered 'dashboard' with 'execution-status' a moment later.
  useEffect(() => {
    if (
      mode === 'evaluation' &&
      observeActive &&
      !FlowObserverPanelEngine.hasUserSelectedTab()
    ) {
      FlowObserverPanelEngine.activateTab('execution-status');
    }
  }, [mode, observeActive]);

  const handleObserveToggle = () => {
    const next = !observeActive;
    setObserveActive(next);
    if (next) {
      // Same §5.1 gate as the mode-change effect above — don't yank the user
      // off a tab (e.g. Dashboard) they already picked this session.
      if (!FlowObserverPanelEngine.hasUserSelectedTab()) {
        FlowObserverPanelEngine.activateTab('execution-status');
      }
    } else {
      useFlowObserverPanelStore.getState().setPanelMode('collapsed');
    }
  };

  // Local state
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [showClearConfirm, setShowClearConfirm] = React.useState(false);
  const [isExecuting, setIsExecuting] = React.useState(false);
  const [viewMenuOpen, setViewMenuOpen] = React.useState(false);
  const [editMenuOpen, setEditMenuOpen] = React.useState(false);
  const [templateMenuOpen, setTemplateMenuOpen] = React.useState(false);
  const [resIdCopied, setResIdCopied] = React.useState(false);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = React.useState(false);
  const [showManageTemplatesModal, setShowManageTemplatesModal] = React.useState(false);
  const [scopeContext, setScopeContext] = React.useState<ScopeContext | null>(null);
  const [templateMode, setTemplateMode] = React.useState<'create' | 'edit'>('create');
  const [existingTemplate, setExistingTemplate] = React.useState<ExecutionTemplate | null>(null);
  const [templateLoading, setTemplateLoading] = React.useState(false);
  const [downloadMenuOpen, setDownloadMenuOpen] = React.useState(false);
  const [showDownloadModal, setShowDownloadModal] = React.useState(false);
  const [executeMenuOpen, setExecuteMenuOpen] = React.useState(false);
  const [showAdvancedExecuteModal, setShowAdvancedExecuteModal] = React.useState(false);
  const [downloadModalEntity, setDownloadModalEntity] = React.useState<{
    type: 'process' | 'app' | 'processthread' | 'processelement';
    id: number;
    name: string;
  } | null>(null);

  // Shared client for both the editor and the manage-list modal below.
  const templateApiClient = React.useMemo(
    () => new ExecutionPipelinesApiClient({ baseUrl: apiConfig.baseUrl }),
    []
  );

  const displayId = workflow?.resId || (currentProcessThreadId != null ? String(currentProcessThreadId) : undefined);

  const handleCopyResId = React.useCallback(() => {
    if (!displayId) return;
    navigator.clipboard.writeText(displayId).then(() => {
      setResIdCopied(true);
      setTimeout(() => setResIdCopied(false), 2000);
    });
  }, [displayId]);

  // Spec §1: 3-way scope resolution based on current Studio context
  // Priority: Node > Thread > Process. Shared by both the "Create Execution
  // Template" and "Manage Execution Templates" menu actions below.
  const resolveCurrentScope = React.useCallback((): ScopeContext | null => {
    if (!workflow) return null;

    // 1. Check if node is selected on canvas
    if (selectedNodeIds && selectedNodeIds.length > 0 && nodes && nodes.length > 0) {
      const selectedNode = nodes.find(n => n.id === selectedNodeIds[0]);
      if (selectedNode) {
        return {
          executionScope: 'ProcessElement',
          scopeReferenceID: selectedNode.id ? parseInt(selectedNode.id, 10) : 0,
          scopeLabel: `${selectedNode.data?.displayName || 'Node'} (ProcessElement)`,
        };
      }
    }

    // 2. Check if thread is open (thread context available)
    if (currentProcessThreadId && currentProcessThreadId > 0) {
      return {
        executionScope: 'ProcessThread',
        scopeReferenceID: currentProcessThreadId,
        scopeLabel: `Thread: ${currentProcessThreadId} (ProcessThread)`,
      };
    }

    // 3. Fallback to process level (top-level workflow)
    return {
      executionScope: 'Process',
      scopeReferenceID: parseInt(workflow.processId, 10) || 0,
      scopeLabel: `${workflow.name} (Process)`,
    };
  }, [workflow, nodes, selectedNodeIds, currentProcessThreadId]);

  // "Create Execution Template" menu item: always opens a fresh template for the
  // current scope. Deliberately does NOT auto-redirect to an existing template
  // for that scope (the old single-button behavior) -- with multiple templates
  // now possible per scope, silently editing "whichever one happened to be
  // first" is surprising. Use "Manage Execution Templates" to edit an existing one.
  const handleCreateNewTemplate = React.useCallback(() => {
    const scope = resolveCurrentScope();
    if (!scope) return;

    setTemplateMenuOpen(false);
    setScopeContext(scope);
    setTemplateMode('create');
    setExistingTemplate(null);
    setShowCreateTemplateModal(true);
  }, [resolveCurrentScope]);

  // "Manage Execution Templates" menu item: lists every template saved at the
  // current scope so the user can open any of them for editing.
  const handleOpenManageTemplates = React.useCallback(() => {
    const scope = resolveCurrentScope();
    if (!scope) return;

    setTemplateMenuOpen(false);
    setScopeContext(scope);
    setShowManageTemplatesModal(true);
  }, [resolveCurrentScope]);

  const handleEditTemplateFromManageList = React.useCallback((template: ExecutionTemplate) => {
    setShowManageTemplatesModal(false);
    setTemplateMode('edit');
    setExistingTemplate(template);
    setShowCreateTemplateModal(true);
  }, []);

  const viewMenuRef = React.useRef<HTMLDivElement>(null);
  const editMenuRef = React.useRef<HTMLDivElement>(null);
  const downloadMenuRef = React.useRef<HTMLDivElement>(null);
  const templateMenuRef = React.useRef<HTMLDivElement>(null);
  const executeMenuRef = React.useRef<HTMLDivElement>(null);
  // Portaled to document.body (see viewMenuPortalRef below) so it always renders above
  // the Observer panel docked at the bottom of the canvas, regardless of stacking context.
  const viewMenuPortalRef = React.useRef<HTMLDivElement>(null);
  const [viewMenuPos, setViewMenuPos] = React.useState<{ top: number; left: number } | null>(null);

  React.useLayoutEffect(() => {
    if (!viewMenuOpen) {
      setViewMenuPos(null);
      return;
    }
    const recalc = () => {
      const btn = viewMenuRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      setViewMenuPos({ top: rect.bottom + 4, left: rect.left });
    };
    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, [viewMenuOpen]);

  // Only one toolbar menu may be open at a time: close every menu first, then open the
  // requested one unless it was already open (in which case clicking its trigger toggles it closed).
  const closeAllMenus = () => {
    setEditMenuOpen(false);
    setDownloadMenuOpen(false);
    setViewMenuOpen(false);
    setTemplateMenuOpen(false);
    setExecuteMenuOpen(false);
  };

  const toggleMenu = (isOpen: boolean, setOpen: (open: boolean) => void) => {
    closeAllMenus();
    if (!isOpen) setOpen(true);
  };

  const handleToggleCollapseAll = React.useCallback(() => {
    if (allNodesCollapsed) {
      window.dispatchEvent(new CustomEvent('flow-studio:expand-all'));
      setAllNodesCollapsed(false);
    } else {
      window.dispatchEvent(new CustomEvent('flow-studio:collapse-all'));
      setAllNodesCollapsed(true);
    }
    setViewMenuOpen(false);
  }, [allNodesCollapsed, setAllNodesCollapsed]);


  // Handle save using store's API integration
  const handleSave = React.useCallback(async () => {
    if (!isDirty || isSaving) {
      return;
    }

    const result = await saveWorkflowToApi();

    if (result) {
      // Success

      // Build detailed summary message
      const s = result.summary;
      const parts: string[] = [];

      if (s) {
        // Nodes
        if (s.nodesCreated > 0) parts.push(`${s.nodesCreated} node${s.nodesCreated > 1 ? 's' : ''} created`);
        if (s.nodesUpdated > 0) parts.push(`${s.nodesUpdated} node${s.nodesUpdated > 1 ? 's' : ''} updated`);
        if (s.nodesDeleted > 0) parts.push(`${s.nodesDeleted} node${s.nodesDeleted > 1 ? 's' : ''} deleted`);

        // Edges (Connections)
        if (s.edgesCreated > 0) parts.push(`${s.edgesCreated} connection${s.edgesCreated > 1 ? 's' : ''} created`);
        if (s.edgesUpdated > 0) parts.push(`${s.edgesUpdated} connection${s.edgesUpdated > 1 ? 's' : ''} updated`);
        if (s.edgesDeleted > 0) parts.push(`${s.edgesDeleted} connection${s.edgesDeleted > 1 ? 's' : ''} deleted`);
      }

      const message = parts.length > 0
        ? `Workflow saved successfully! ${parts.join(', ')}.`
        : 'Workflow saved successfully!';

      showToast('success', message);
    } else if (saveError) {
      // Error
      console.error('Save failed:', saveError);
      showToast('error', `Save failed: ${saveError}`);
    }
  }, [isDirty, isSaving, saveWorkflowToApi, saveError, showToast]);

  // Handle execute workflow
  // `observabilityOverride` is optional: the plain Execute button (and split-button's
  // left half) calls this with no argument, running under whatever the resolved default
  // is (unchanged behavior). The Advanced Execute modal calls it with a built override
  // for this one run — see AdvancedExecuteModal.tsx and ObservabilityOverride's doc
  // comment in processEngineExecution.types.ts for the shape/contract.
  const handleExecute = React.useCallback(async (observabilityOverride?: ObservabilityOverride) => {
    if (!workflow || isExecuting) {
      return;
    }

    if (!nodes || nodes.length === 0) {
      showToast('error', 'Cannot execute: the workflow has no nodes. Add at least one node to the canvas.');
      return;
    }

    // Dev-test mock mode — no backend to execute against
    if (currentProcessThreadId === -1) {
      showToast('info', 'Execution not available in dev-test mock mode');
      return;
    }

    setIsExecuting(true);

    try {
      // Save workflow if auto-save enabled and dirty
      if (settings.autoSave && isDirty) {
        await saveWorkflowToApi();
      }

      // Generate a GUID for the execution (resID) — client-generated so we can join the
      // SignalR execution group BEFORE firing /execute below (see join-before-execute note).
      const generateGUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = Math.random() * 16 | 0;
          const v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      };
      const clientResID = generateGUID();

      // Build ProcessExecutionContext according to backend structure
      // Note: BaseApiClient.post() will wrap this in { metadata, data } envelope
      const executorID = {
        appID: 1, // TODO: Get from app context (for now set to 0)
        processID: parseInt(workflow.processId, 10) > 0 ? parseInt(workflow.processId, 10) : undefined,
        processThreadID: currentProcessThreadId != null && currentProcessThreadId > 0 ? currentProcessThreadId : undefined,
        processElementID: undefined, // Will be set if specific element is being executed
        processThreadVersionID: currentVersionId != null && currentVersionId > 0 ? currentVersionId : undefined,
      };

      const executionID = {
        resID: clientResID,
        ID: undefined, // Will be set by backend with numeric ID
      };

      const mode: ProcessContextExecutionMode = {
        executionMode: ExecutionMode.Manual, // User-triggered execution
        environmentMode: EnvironmentMode.Default, // Use default environment (Production)
        designerMode: DesignerMode.Live, // Execute in live mode
        startedAtUtc: new Date().toISOString(),
      };

      // Execute request is the ProcessExecutionContext itself
      // BaseApiClient.post() will wrap it with metadata envelope
      const executeRequest: ExecuteProcessRequest = {
        executorID,
        executionID,
        mode,
        inputData: {}, // Can be populated from workflow inputs
        triggerData: undefined,
        requestSession: undefined, // Will be set by backend from context
        memory: undefined,
        threadContexts: [],
        currentExecutionState: 0, // eExecutionState.Running
      };

      if (observabilityOverride) {
        executeRequest.observabilityOverride = observabilityOverride;
      }

      // Join-before-execute: switch to execution mode and start tracking with the
      // client-generated resID NOW, before the /execute POST goes out. This flips
      // useExecutionSignalR's shouldConnect condition immediately, so the SignalR connect +
      // JoinExecution round trip runs concurrently with (and, once awaited below, strictly
      // before) the backend actually starting the workflow. Without this, on a remote host
      // (real network RTT for negotiate/upgrade/handshake/join) a fast workflow can finish and
      // broadcast all its events before the client ever joins the group — the client then falls
      // back to a coarse DB catch-up (final per-node status only, no debug-level progress
      // events) instead of seeing the full live stream it gets on localhost.
      startExecution({ ResID: clientResID, ID: undefined });
      setMode('execution');
      setObserveActive(true);
      FlowObserverPanelEngine.activateTab('dashboard');

      // Wait for JoinExecution to be confirmed (useExecutionSignalR sets executionGroupJoined),
      // polling briefly rather than blocking indefinitely — if the hub is unreachable or slow
      // beyond this budget, proceed anyway and let the existing DB catch-up cover it, rather than
      // stall the user's Execute click on a hung connection.
      const JOIN_WAIT_TIMEOUT_MS = 3000;
      const JOIN_WAIT_POLL_INTERVAL_MS = 50;
      const joinDeadline = Date.now() + JOIN_WAIT_TIMEOUT_MS;
      while (
        !useDesignerModeStore.getState().executionGroupJoined &&
        Date.now() < joinDeadline
      ) {
        await new Promise((resolve) => setTimeout(resolve, JOIN_WAIT_POLL_INTERVAL_MS));
      }
      if (!useDesignerModeStore.getState().executionGroupJoined) {
        console.warn('[Execute] SignalR group join did not confirm within', JOIN_WAIT_TIMEOUT_MS, 'ms — proceeding anyway, DB catch-up will cover any missed live events.');
      }

      const response = await processEngineExecutionApiClient.executeProcess(executeRequest, {
        timeout: EXECUTE_WORKFLOW_TIMEOUT_MS,
      });

      // Check if response has data (execution result)
      if (response.data) {
        let responseData = response.data as any;

        // Extract execution ID from response (backend returns camelCase)
        const executionId = responseData.executionID ?? responseData.ExecutionID;
        const executionState = responseData.state?.currentExecutionState ?? responseData.State?.CurrentExecutionState;

        // Extract resID and id (camelCase from backend)
        const resID = executionId?.resID ?? executionId?.ResID;
        const numericId = executionId?.id ?? executionId?.ID ?? executionId?.Id;

        if (executionId && resID) {
          // Mode/tracking/observer panel were already set up before the POST (see above) using
          // the same client-generated resID — just patch in the backend's numeric ID. Do NOT
          // call startExecution() again here: it resets nodeExecutionStates/nodeInspectorData,
          // which may already hold live data received while this response was in flight.
          if (numericId != null) {
            setExecutionNumericId(numericId);
          }

          // API is asynchronous (fire and forget) - returns immediately with execution ID
          // Actual execution happens in background, monitored via SignalR + polling
          // ExecutionData is NOT included in the response (will be available via SignalR events)
          // Show notification (display resID GUID)
          notifications.notifyExecutionStarted(resID);
        } else {
          // Provide detailed error info for debugging
          console.error(' Execution response missing required data:', {
            hasExecutionId: !!executionId,
            executionId: executionId,
            executionIdKeys: executionId ? Object.keys(executionId) : [],
            hasResID: !!resID,
            resID,
            hasExecutionState: !!executionState,
            fullResponseData: responseData,
          });

          const missingField = !executionId
            ? 'ExecutionID'
            : !resID
              ? 'ResID'
              : 'Unknown';

          notifications.notifyExecutionError(
            `Tracking failed - Missing: ${missingField}`,
            'Execution started but tracking failed'
          );
          // No valid execution to track despite the optimistic join-before-execute setup above —
          // revert to design mode rather than leaving the UI stuck in execution mode for nothing.
          clearExecutionState();
          setMode('design');
        }
      } else if (response.error) {
        console.error('Execution failed:', response.error);
        // A client-side abort (code 'TIMEOUT') does NOT mean the workflow failed — the backend
        // Execute call is synchronous and keeps running to completion in the background even
        // after the browser gives up waiting on the HTTP response (see EXECUTE_WORKFLOW_TIMEOUT_MS
        // comment above). Tell the user that explicitly instead of implying the run itself failed.
        // Stay in execution mode here (don't roll back): the client already joined the SignalR
        // group for clientResID before the POST went out, so a genuinely still-running backend
        // execution will keep delivering live events to it regardless of this HTTP timeout.
        if (response.error.code === 'TIMEOUT') {
          notifications.notifyExecutionError(
            `No response after ${Math.round(EXECUTE_WORKFLOW_TIMEOUT_MS / 60000)} minutes. The workflow may still be running in the background — check Execution Logs / Dashboard for its status.`,
            'Execution request timed out'
          );
        } else {
          notifications.notifyExecutionError(
            response.error.message,
            'Execution failed'
          );
          // The /execute call genuinely failed (not a timeout) — no execution actually started
          // server-side, so revert the optimistic mode/tracking state from before the POST.
          clearExecutionState();
          setMode('design');
        }
      } else {
        console.error('Execution failed with unknown error');
        notifications.notifyExecutionError(
          'Unknown error',
          'Execution failed'
        );
        clearExecutionState();
        setMode('design');
      }
    } catch (error: any) {
      console.error('Execute error:', error);
      notifications.notifyExecutionError(
        error.message || 'Unknown error',
        'Execution error'
      );
      // Thrown before/during the request (not the backend's own TIMEOUT response path above) —
      // no execution actually started, so revert the optimistic mode/tracking state.
      clearExecutionState();
      setMode('design');
    } finally {
      setIsExecuting(false);
    }
  }, [
    workflow,
    currentProcessThreadId,
    isExecuting,
    settings.autoSave,
    isDirty,
    saveWorkflowToApi,
    startExecution,
    setExecutionResult,
    setExecutionNumericId,
    clearExecutionState,
    setMode,
    notifications,
  ]);

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => {
          setIsFullscreen(false);
        });
      }
    }
  };

  // Close dropdowns on outside click
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        viewMenuRef.current && !viewMenuRef.current.contains(target) &&
        !(viewMenuPortalRef.current && viewMenuPortalRef.current.contains(target))
      ) {
        setViewMenuOpen(false);
      }
      if (editMenuRef.current && !editMenuRef.current.contains(target)) {
        setEditMenuOpen(false);
      }
      if (templateMenuRef.current && !templateMenuRef.current.contains(target)) {
        setTemplateMenuOpen(false);
      }
      if (executeMenuRef.current && !executeMenuRef.current.contains(target)) {
        setExecuteMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close dropdowns when the canvas pane is clicked. React Flow's pan handler calls
  // stopImmediatePropagation() on pane mousedown (to support panOnDrag), so clicks on
  // the canvas background never reach the document-level listener above.
  React.useEffect(() => {
    const handlePaneClick = () => {
      setViewMenuOpen(false);
      setEditMenuOpen(false);
      setExecuteMenuOpen(false);
    };
    window.addEventListener('flow-studio:pane-click', handlePaneClick);
    return () => window.removeEventListener('flow-studio:pane-click', handlePaneClick);
  }, []);

  // Listen for fullscreen changes
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Listen for Ctrl+S to save
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Execute split button: left half runs `handleExecute()` immediately with no argument
  // (current default, unchanged behavior); the chevron opens a one-item menu whose
  // "Advanced Execute" action opens AdvancedExecuteModal, which in turn calls
  // `handleExecute(override)` with a user-built ObservabilityOverride. Shared between
  // the design-mode Execute button and the execution-mode "idle" Execute button, which
  // both call the same handleExecute.
  const renderExecuteSplitButton = (title: string) => (
    <div className="toolbar-dropdown execute-split-button" ref={executeMenuRef}>
      <button
        className="top-toolbar-button top-toolbar-button--primary execute-split-button-main"
        onClick={() => handleExecute()}
        disabled={!workflow || isExecuting}
        title={!workflow ? "No workflow loaded" : isExecuting ? "Executing..." : title}
        aria-label={isExecuting ? "Executing workflow" : "Execute workflow"}
      >
        {isExecuting ? (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
              <path d="M21 12a9 9 0 11-6.219-8.56"/>
            </svg>
            Executing...
          </>
        ) : (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
            Execute
          </>
        )}
      </button>
      <button
        className="top-toolbar-button top-toolbar-button--primary toolbar-dropdown-trigger execute-split-button-trigger"
        onClick={() => toggleMenu(executeMenuOpen, setExecuteMenuOpen)}
        disabled={!workflow || isExecuting}
        aria-label="Advanced execute options"
        title="Advanced Execute..."
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {executeMenuOpen && (
        <div className={`toolbar-dropdown-menu ${theme === 'dark' ? 'toolbar-dropdown-menu--dark' : 'toolbar-dropdown-menu--light'}`}>
          <button
            className="toolbar-dropdown-item"
            onClick={() => {
              setExecuteMenuOpen(false);
              setShowAdvancedExecuteModal(true);
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.65 1.65 0 004.6 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 4.6a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
            </svg>
            Advanced Execute
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className={`top-toolbar ${theme === 'dark' ? 'top-toolbar--dark' : 'top-toolbar--light'}`}>
      {/* Workflow Name + ResID */}
      <div className={`top-toolbar-workflow-name ${theme === 'dark' ? 'top-toolbar-workflow-name--dark' : 'top-toolbar-workflow-name--light'}`}>
        <span>{workflow?.name || 'Untitled Workflow'}</span>
        {displayId && (
          <span
            className="top-toolbar-res-id"
            title={`Workflow ${workflow?.resId ? 'ResID' : 'ID'}: ${displayId} — click to copy`}
            onClick={handleCopyResId}
            style={{
              marginLeft: '8px',
              fontSize: '11px',
              fontFamily: 'monospace',
              opacity: 0.6,
              cursor: 'pointer',
              userSelect: 'none',
              padding: '1px 5px',
              borderRadius: '3px',
              background: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
            onMouseLeave={e => (e.currentTarget.style.opacity = '0.6')}
          >
            {resIdCopied ? '✓ Copied' : (workflow?.resId ? `${workflow.resId.slice(0, 8)}…` : `#${displayId}`)}
          </span>
        )}
      </div>

      {/* Mode Selector */}
      <ModeSelector />

      {/* Edit Dropdown - design mode only */}
      {mode === 'design' && (
        <div className="toolbar-dropdown" ref={editMenuRef}>
          <button
            className={`top-toolbar-button toolbar-dropdown-trigger ${theme === 'dark' ? 'top-toolbar-button--dark' : 'top-toolbar-button--light'}`}
            onClick={() => toggleMenu(editMenuOpen, setEditMenuOpen)}
            aria-label="Edit menu"
          >
            Edit
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          {editMenuOpen && (
            <div className={`toolbar-dropdown-menu ${theme === 'dark' ? 'toolbar-dropdown-menu--dark' : 'toolbar-dropdown-menu--light'}`}>
              <button
                className="toolbar-dropdown-item"
                onClick={() => { undo(); setEditMenuOpen(false); }}
                disabled={!canUndo()}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 00-4-4H4"/>
                </svg>
                Undo
                <span className="toolbar-dropdown-shortcut">Ctrl+Z</span>
              </button>
              <button
                className="toolbar-dropdown-item"
                onClick={() => { redo(); setEditMenuOpen(false); }}
                disabled={!canRedo()}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 14 20 9 15 4"/><path d="M4 20v-7a4 4 0 014-4h12"/>
                </svg>
                Redo
                <span className="toolbar-dropdown-shortcut">Ctrl+⇧Z</span>
              </button>

              <div style={{ height: '1px', background: theme === 'dark' ? 'rgba(255,255,255,0.07)' : '#e5e7eb', margin: '3px 6px' }} />

              <button
                className="toolbar-dropdown-item"
                style={autoSaveEnabled ? { color: '#22c55e' } : { color: '#ef4444' }}
                onClick={() => { toggleAutoSave(); setEditMenuOpen(false); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
                </svg>
                Auto Save ({autoSaveEnabled ? 'On' : 'Off'})
              </button>

              {mode === 'design' && (
                <>
                  <div style={{ height: '1px', background: theme === 'dark' ? 'rgba(255,255,255,0.07)' : '#e5e7eb', margin: '3px 6px' }} />
                  <button
                    className="toolbar-dropdown-item"
                    style={{ color: '#ef4444' }}
                    onClick={() => { setShowClearConfirm(true); setEditMenuOpen(false); }}
                  >
                    <TrashIcon size={14} />
                    Clear All
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Download Dropdown - design mode only */}
      {mode === 'design' && (
        <div className="toolbar-dropdown" ref={downloadMenuRef}>
          <button
            className={`top-toolbar-button toolbar-dropdown-trigger ${theme === 'dark' ? 'top-toolbar-button--dark' : 'top-toolbar-button--light'}`}
            onClick={() => toggleMenu(downloadMenuOpen, setDownloadMenuOpen)}
            aria-label="Download menu"
          >
            Download
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          {downloadMenuOpen && (
            <div className={`toolbar-dropdown-menu ${theme === 'dark' ? 'toolbar-dropdown-menu--dark' : 'toolbar-dropdown-menu--light'}`}>
              <button
                className="toolbar-dropdown-item"
                onClick={() => {
                  if (workflow?.processId) {
                    setDownloadModalEntity({
                      type: 'process',
                      id: parseInt(workflow.processId, 10),
                      name: workflow.name || 'Process'
                    });
                    setShowDownloadModal(true);
                  }
                  setDownloadMenuOpen(false);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Process
              </button>
              <button
                className="toolbar-dropdown-item"
                onClick={() => {
                  if (currentProcessThreadId && currentProcessThreadId > 0) {
                    setDownloadModalEntity({
                      type: 'processthread',
                      id: currentProcessThreadId,
                      name: workflow?.name || 'Process Thread'
                    });
                    setShowDownloadModal(true);
                  }
                  setDownloadMenuOpen(false);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Process Thread
              </button>
            </div>
          )}
        </div>
      )}

      {/* View Dropdown - always visible, sits right next to Edit in design mode */}
      <div className="toolbar-dropdown" ref={viewMenuRef}>
        <button
          className={`top-toolbar-button toolbar-dropdown-trigger ${theme === 'dark' ? 'top-toolbar-button--dark' : 'top-toolbar-button--light'}`}
          onClick={() => toggleMenu(viewMenuOpen, setViewMenuOpen)}
          aria-label="View menu"
        >
          View
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        {viewMenuOpen && viewMenuPos && createPortal(
          <div
            ref={viewMenuPortalRef}
            className={`toolbar-dropdown-menu ${theme === 'dark' ? 'toolbar-dropdown-menu--dark' : 'toolbar-dropdown-menu--light'}`}
            style={{ position: 'fixed', top: viewMenuPos.top, left: viewMenuPos.left, zIndex: 100000 }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              className="toolbar-dropdown-item"
              onClick={() => { window.dispatchEvent(new CustomEvent('flow-studio:fit-view')); setViewMenuOpen(false); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
              </svg>
              Fit View
            </button>

            <div style={{ height: '1px', background: theme === 'dark' ? 'rgba(255,255,255,0.07)' : '#e5e7eb', margin: '3px 6px' }} />

            <button
              className="toolbar-dropdown-item"
              onClick={() => { toggleFullscreen(); setViewMenuOpen(false); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {isFullscreen
                  ? <><polyline points="8 3 3 3 3 8"/><polyline points="21 8 21 3 16 3"/><polyline points="3 16 3 21 8 21"/><polyline points="16 21 21 21 21 16"/></>
                  : <><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></>
                }
              </svg>
              {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              <span className="toolbar-dropdown-shortcut">F11</span>
            </button>
            <button
              className="toolbar-dropdown-item"
              onClick={() => { setShowMinimap(!showMinimap); setViewMenuOpen(false); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="2"/>
                <rect x="14" y="14" width="6" height="6" rx="1"/>
                {!showMinimap && <line x1="4" y1="4" x2="20" y2="20"/>}
              </svg>
              {showMinimap ? 'Hide Minimap' : 'Show Minimap'}
            </button>

            <div style={{ height: '1px', background: theme === 'dark' ? 'rgba(255,255,255,0.07)' : '#e5e7eb', margin: '3px 6px' }} />

            <button className="toolbar-dropdown-item" onClick={handleToggleCollapseAll}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <line x1="8" y1="12" x2="16" y2="12"/>
                {!allNodesCollapsed && <line x1="12" y1="8" x2="12" y2="16"/>}
              </svg>
              {allNodesCollapsed ? 'Expand All Nodes' : 'Collapse All Nodes'}
            </button>

            <button
              className="toolbar-dropdown-item"
              style={hideSatellites ? { color: '#ef4444' } : undefined}
              onClick={() => {
                const next = !hideSatellites;
                setHideSatellites(next);
                // Showing satellites also reveals any individually self-hidden nodes
                if (!next) (useUIStore.getState() as any).showAllNodes?.();
                setViewMenuOpen(false);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="10" width="6" height="4" rx="1"/>
                <rect x="2" y="9" width="5" height="6" rx="1"/>
                <line x1="7" y1="12" x2="9" y2="12"/>
                <rect x="17" y="9" width="5" height="6" rx="1"/>
                <line x1="15" y1="12" x2="17" y2="12"/>
                <line x1="13" y1="10" x2="16" y2="6"/>
                <circle cx="16.5" cy="5.5" r="1.5"/>
                {hideSatellites && <line x1="3" y1="3" x2="21" y2="21"/>}
              </svg>
              {hideSatellites ? 'Show Satellites' : 'Hide Satellites'}
            </button>

            <button
              className="toolbar-dropdown-item"
              style={hideComponentsGlobally ? { color: '#ef4444' } : undefined}
              onClick={() => { toggleHideComponentsGlobally(); setViewMenuOpen(false); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
                {hideComponentsGlobally && <line x1="3" y1="3" x2="21" y2="21"/>}
              </svg>
              {hideComponentsGlobally ? 'Show Components' : 'Hide Components'}
            </button>

            <button
              className="toolbar-dropdown-item"
              style={hideUnassignedPorts ? { color: '#ef4444' } : undefined}
              onClick={() => { setHideUnassignedPorts(!hideUnassignedPorts); setViewMenuOpen(false); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="5" cy="12" r="2"/>
                <circle cx="19" cy="12" r="2"/>
                <line x1="7" y1="12" x2="17" y2="12"/>
                {hideUnassignedPorts && <line x1="4" y1="4" x2="20" y2="20"/>}
              </svg>
              {hideUnassignedPorts ? 'Show Unassigned Ports' : 'Hide Unassigned Ports'}
            </button>

            <div style={{ height: '1px', background: theme === 'dark' ? 'rgba(255,255,255,0.07)' : '#e5e7eb', margin: '3px 6px' }} />

            <button
              className="toolbar-dropdown-item"
              style={!hilFormsVisible ? { color: '#ef4444' } : undefined}
              onClick={() => { toggleHilFormsVisible(); setViewMenuOpen(false); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="2" width="16" height="20" rx="2"/>
                <line x1="8" y1="7" x2="16" y2="7"/>
                <line x1="8" y1="12" x2="16" y2="12"/>
                <line x1="8" y1="17" x2="12" y2="17"/>
                {!hilFormsVisible && <line x1="2" y1="2" x2="22" y2="22"/>}
              </svg>
              {hilFormsVisible ? 'Hide HIL Forms' : 'Show HIL Forms'}
            </button>

            <button
              className="toolbar-dropdown-item"
              style={hilChatVisible ? { color: '#22c55e' } : undefined}
              onClick={() => { toggleHilChatVisible(); setViewMenuOpen(false); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                {!hilChatVisible && <line x1="2" y1="2" x2="22" y2="22"/>}
              </svg>
              {hilChatVisible ? 'Hide Chat Window' : 'Show Chat Window'}
            </button>

            <div style={{ height: '1px', background: theme === 'dark' ? 'rgba(255,255,255,0.07)' : '#e5e7eb', margin: '3px 6px' }} />

            <button
              className="toolbar-dropdown-item"
              style={nodeResizeEnabled ? { color: '#22c55e' } : undefined}
              onClick={() => { toggleNodeResizeEnabled?.(); setViewMenuOpen(false); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9"/>
                <polyline points="9 21 3 21 3 15"/>
                <line x1="21" y1="3" x2="14" y2="10"/>
                <line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
              {nodeResizeEnabled ? 'Node Resize (On)' : 'Node Resize (Off)'}
            </button>

            <div style={{ margin: '4px 8px', borderTop: '1px solid rgba(128,128,128,0.2)' }} />

            {/* Reset View — resets all global view settings */}
            <button
              className="toolbar-dropdown-item"
              style={{ color: '#ef4444' }}
              onClick={() => {
                setHideSatellites(false);
                setHideUnassignedPorts(false);
                setAllNodesCollapsed(false);
                const s = useUIStore.getState() as any;
                s.showAllNodes?.();
                s.clearNodePortHides?.();
                if (!hideComponentsGlobally) toggleHideComponentsGlobally(); // restore default: hidden
                window.dispatchEvent(new CustomEvent('flow-studio:expand-all'));
                setViewMenuOpen(false);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1 4 1 10 7 10"/>
                <path d="M3.51 15a9 9 0 1 0 .49-4.5"/>
              </svg>
              Reset View
            </button>
          </div>,
          document.body
        )}
      </div>


      {/* Save Button - Always visible */}
      <button
        className={`top-toolbar-button save-button ${theme === 'dark' ? 'top-toolbar-button--dark' : 'top-toolbar-button--light'} ${isSaving ? 'save-button--saving' : (isDirty ? 'save-button--dirty' : 'save-button--saved')}`}
        onClick={handleSave}
        title={isDirty ? "Save Changes (Ctrl+S)" : "No Unsaved Changes"}
        aria-label={isSaving ? "Saving workflow" : (isDirty ? "Save workflow" : "Workflow saved")}
      >
        {isSaving ? <><SaveIcon size={14} /> Saving...</> : (isDirty ? <><SaveIcon size={14} /> Save</> : <><CheckIcon size={14} /> Saved</>)}
      </button>

      {/* Design Mode - Create Template Dropdown */}
      {mode === 'design' && (
        <div className="toolbar-dropdown" ref={templateMenuRef}>
          <button
            className={`top-toolbar-button toolbar-dropdown-trigger ${theme === 'dark' ? 'top-toolbar-button--dark' : 'top-toolbar-button--light'}`}
            onClick={() => toggleMenu(templateMenuOpen, setTemplateMenuOpen)}
            disabled={!workflow}
            title="Create or manage this workflow's reusable execution templates"
            aria-label="Create template menu"
          >
            Create Template
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          {templateMenuOpen && (
            <div className={`toolbar-dropdown-menu ${theme === 'dark' ? 'toolbar-dropdown-menu--dark' : 'toolbar-dropdown-menu--light'}`}>
              <button className="toolbar-dropdown-item" onClick={handleCreateNewTemplate}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Create Execution Template
              </button>
              <button className="toolbar-dropdown-item" onClick={handleOpenManageTemplates}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                  <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                </svg>
                Manage Execution Templates
              </button>
            </div>
          )}
        </div>
      )}

      {/* Design Mode - Execute Split Button (Execute + Advanced Execute chevron) */}
      {mode === 'design' && renderExecuteSplitButton('Execute Workflow')}

      {/* Execution Mode - Execution Control Buttons */}
      {mode === 'execution' && (
        <>
          {/* Execute Split Button - Show when idle (waiting to start) */}
          {executionState === 'idle' && renderExecuteSplitButton('Start Execution')}

          {/* Resume Button - Show when paused */}
          {executionState === 'paused' && (
            <button
              className="top-toolbar-button top-toolbar-button--resume"
              onClick={handleResume}
              title="Resume Execution"
            >
               Resume
            </button>
          )}

          {/* Pause Button - Show when running or waiting */}
          {(executionState === 'running' || executionState === 'waiting') && (
            <button
              className="top-toolbar-button top-toolbar-button--pause"
              onClick={handlePause}
              title="Pause Execution"
            >
               Pause
            </button>
          )}

          {/* Cancel Button - Show when execution is active */}
          {(executionState === 'running' || executionState === 'paused' || executionState === 'waiting') && (
            <button
              className="top-toolbar-button top-toolbar-button--cancel"
              onClick={handleCancel}
              title="Cancel Execution"
            >
               Cancel
            </button>
          )}

          {/* New Execution Button - Show when execution is complete */}
          {(executionState === 'completed' ||
            executionState === 'failed' ||
            executionState === 'cancelled' ||
            executionState === 'completedWithWarnings' ||
            executionState === 'timedOut') && (
            <button
              className="top-toolbar-button top-toolbar-button--new-execution"
              onClick={() => {
                clearExecutionState();
                setMode('design');
                showToast('info', 'Ready for a new execution');
              }}
              title="Start a new execution"
            >
              <ZapIcon size={14} /> New Execution
            </button>
          )}

          {/* Back to Design - Use Mode Selector instead of redundant button */}
        </>
      )}

      {/* Observe Button - execution and evaluation modes */}
      {(mode === 'execution' || mode === 'evaluation') && (
        <button
          className={`top-toolbar-button top-toolbar-button--observer ${observeActive ? 'top-toolbar-button--active' : ''} ${theme === 'dark' ? 'top-toolbar-button--dark' : 'top-toolbar-button--light'}`}
          onClick={handleObserveToggle}
          title={observeActive ? 'Close Observe Panel' : 'Open Observe Panel'}
          aria-label="Toggle observe panel"
          aria-pressed={observeActive}
        >
          <EyeIcon size={14} />
          Observe
        </button>
      )}

      {/* Manage Execution Templates - lists every template saved at the current scope */}
      <ManageExecutionTemplatesModal
        isOpen={showManageTemplatesModal}
        onClose={() => setShowManageTemplatesModal(false)}
        apiClient={templateApiClient}
        scope={scopeContext}
        onEditTemplate={handleEditTemplateFromManageList}
        onCreateNew={() => {
          setShowManageTemplatesModal(false);
          handleCreateNewTemplate();
        }}
      />

      {/* Unified Template Editor Modal - Check if exists, edit if found, create if new */}
      {showCreateTemplateModal && scopeContext && (
        <TemplateEditorModal
          isOpen={showCreateTemplateModal}
          mode={templateMode}
          template={templateMode === 'edit' ? existingTemplate : null}
          scope={templateMode === 'create' ? {
            scopeLabel: scopeContext.scopeLabel,
            executionScope: scopeContext.executionScope,
            scopeReferenceID: scopeContext.scopeReferenceID,
          } as ModalScopeContext : undefined}
          apiClient={templateApiClient}
          onClose={() => {
            setShowCreateTemplateModal(false);
            setScopeContext(null);
            setTemplateMode('create');
            setExistingTemplate(null);
          }}
          onSuccess={() => {
            setShowCreateTemplateModal(false);
            setScopeContext(null);
            setTemplateMode('create');
            setExistingTemplate(null);
          }}
          onShowToast={(type, message) => {
            // Handle both execution and creation toasts
            if (type === 'success') {
              showToast('success', message);
            } else if (type === 'error') {
              showToast('error', message);
            }
          }}
          // Execute handler: Use Flow Studio's SignalR-based execution flow
          onExecute={(executionID, templateName) => {
            // Create normalized execution ID for Flow Studio
            const normalizedExecutionId = {
              ResID: executionID,  // GUID string
              ID: undefined,        // Will be set by backend via SignalR
            };

            // Start tracking in Flow Studio's store
            startExecution(normalizedExecutionId);

            // Switch to execution mode
            setMode('execution');

            // Auto-enable Observer Panel with real-time updates
            setObserveActive(true);
            FlowObserverPanelEngine.activateTab('dashboard');

            // Close modal after execution starts
            setShowCreateTemplateModal(false);
            setScopeContext(null);
            setTemplateMode('create');
            setExistingTemplate(null);

            // Notify user
            notifications.notifyExecutionStarted(executionID);
          }}
        />
      )}

      {/* Clear All Confirmation Dialog */}
      {showClearConfirm && (
        <ConfirmDialog
          isOpen={showClearConfirm}
          onClose={() => {
            console.log('ConfirmDialog onClose called');
            setShowClearConfirm(false);
          }}
          onConfirm={() => {
            console.log('ConfirmDialog onConfirm called - clearing workflow');

            // Push current state to history for undo
            pushHistory();

            // Clear all nodes and edges
            setNodes([]);
            setEdges([]);

            // Mark workflow as dirty
            markDirty();

            setShowClearConfirm(false);

            if (showToast) {
              showToast('success', 'Workflow cleared successfully');
            }
          }}
          title="Clear All?"
          message="Are you sure you want to remove all nodes and connections from the workflow? This action cannot be undone."
          confirmText="Clear All"
          type="danger"
        />
      )}

      {/* Advanced Execute Modal - observability profile / custom overrides for this run */}
      <AdvancedExecuteModal
        isOpen={showAdvancedExecuteModal}
        onClose={() => setShowAdvancedExecuteModal(false)}
        onExecute={(override: ObservabilityOverride) => {
          handleExecute(override);
        }}
      />

      {/* Download Flow Entity Modal */}
      {showDownloadModal && downloadModalEntity && (
        <DownloadFlowEntityModal
          isOpen={showDownloadModal}
          onClose={() => setShowDownloadModal(false)}
          entityType={downloadModalEntity.type}
          entityId={downloadModalEntity.id}
          entityName={downloadModalEntity.name}
          apiBaseUrl={apiConfig.baseUrl}
        />
      )}

    </div>
  );
};
